import { useState } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as guestApi from '../../api/guest';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Field } from '../common/Field';
import { Input, Select } from '../common/Input';
import { Skeleton } from '../common/Skeleton';
import { apiError } from '../../utils/apiError';
import { formatDateTime, parseServerDate } from '../../utils/datetime';
import { GuestPasscode } from '../../types';

const EXPIRY_OPTIONS = [
  { value: '1h',   label: '1 hour' },
  { value: '24h',  label: '24 hours' },
  { value: '7d',   label: '7 days' },
  { value: '30d',  label: '30 days' },
  { value: 'never', label: 'Never' },
];

function PasscodeStatus({ p }: { p: GuestPasscode }) {
  const cls = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';
  if (p.revoked_at) {
    return <span className={`${cls} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300`}>Revoked</span>;
  }
  const expiry = parseServerDate(p.expires_at);
  if (expiry && expiry <= new Date()) {
    return <span className={`${cls} bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}>Expired</span>;
  }
  return <span className={`${cls} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300`}>Active</span>;
}

function formatExpiry(p: GuestPasscode): string {
  if (!p.expires_at) return 'Never';
  return formatDateTime(p.expires_at);
}

export function GuestPasscodesPage() {
  useDocumentTitle('Guest passcodes');
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [expiry, setExpiry] = useState('7d');
  const [createError, setCreateError] = useState('');
  const [newCode, setNewCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<GuestPasscode | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['guest-passcodes'],
    queryFn: () => guestApi.listPasscodes().then(r => r.passcodes),
  });

  const createMutation = useMutation({
    mutationFn: guestApi.createPasscode,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['guest-passcodes'] });
      setNewCode(data.passcode.code);
      setLabel('');
      setCreateError('');
    },
    onError: (err) => setCreateError(apiError(err, 'Failed to create passcode')),
  });

  const revokeMutation = useMutation({
    mutationFn: guestApi.revokePasscode,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['guest-passcodes'] });
      setRevoking(null);
    },
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    setNewCode(null);
    createMutation.mutate({ label: label.trim(), expiry });
  }

  function handleCopy() {
    if (!newCode) return;
    navigator.clipboard.writeText(newCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Guest passcodes</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Give read-only dashboard access without creating a user account.
        </p>
      </div>

      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Create a passcode</h2>

        <form onSubmit={handleCreate} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[12rem]">
            <Field label="Label" required hint="Who or what is this for?">
              {p => (
                <Input
                  {...p}
                  type="text"
                  placeholder="e.g. Client review, Team share…"
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  required
                />
              )}
            </Field>
          </div>
          <Field label="Expires">
            {p => (
              <Select {...p} value={expiry} onChange={e => setExpiry(e.target.value)} className="w-auto">
                {EXPIRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            )}
          </Field>
          <Button type="submit" loading={createMutation.isPending} disabled={!label.trim()}>
            {createMutation.isPending ? 'Creating…' : 'Generate'}
          </Button>
        </form>

        {createError && <div className="mt-3"><Alert tone="error">{createError}</Alert></div>}

        {newCode && (
          <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs text-green-800 dark:text-green-300 font-medium mb-1">
                New passcode — copy it now, it is not shown again
              </p>
              <p className="font-mono text-xl tracking-widest text-green-900 dark:text-green-200 select-all">
                {newCode}
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        )}
      </Card>

      <Card>
        {isLoading ? (
          <div className="p-4 space-y-3" aria-busy="true">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : !data?.length ? (
          <p className="p-8 text-center text-gray-500 dark:text-gray-400">No passcodes yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[48rem]">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  {['Label', 'Code', 'Status', 'Expires', 'Uses', 'Created by'].map(h => (
                    <th key={h} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {data.map((p: GuestPasscode) => (
                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-6 py-4 text-sm font-medium">{p.label}</td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm tracking-wider text-gray-700 dark:text-gray-300">{p.code}</span>
                    </td>
                    <td className="px-6 py-4"><PasscodeStatus p={p} /></td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 tabular-nums">{formatExpiry(p)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 tabular-nums">{p.use_count}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{p.created_by_name ?? '—'}</td>
                    <td className="px-6 py-4 text-right">
                      {!p.revoked_at && (
                        <Button variant="ghost" size="sm" onClick={() => setRevoking(p)}>Revoke</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!revoking}
        title="Revoke passcode"
        message={`"${revoking?.label}" will stop working immediately for anyone holding it. This cannot be undone.`}
        confirmLabel="Revoke passcode"
        loading={revokeMutation.isPending}
        onConfirm={() => revoking && revokeMutation.mutate(revoking.id)}
        onClose={() => setRevoking(null)}
      />
    </div>
  );
}
